import { ContactShadows, Environment, OrbitControls } from '@react-three/drei';
import studioHdr from '../../../assets/react-three-enviorment/studio_small_03_1k.hdr';
import { useFrame, useThree } from '@react-three/fiber';
import React, { useRef } from 'react';
import {
  cameraKeyframes,
  leftInsoleKeyframes,
  rightInsoleKeyframes,
} from './Me3DAnimationFrames';
import { GlbInsoleView } from './GlbInsoleView';
import { AnnotationArrows } from './AnnotationArrows/Main';
import { getInsoleGlb } from '../../../constants/glb-assets';
import { FoamPackageType } from '../../../constants/foot';
import { useResponsive } from '../../../hooks/useResponsive';
import { sceneTunnel } from '../../insole-studio/sceneTunnel';
import { CameraSetter } from '../../insole-studio/CameraSetter';

const TRANSITION_DURATION = 0.8; // seconds (0.6–1.0 feels good)

const clamp = (x: number, min: number, max: number) =>
  Math.min(Math.max(x, min), max);

const Scene = ({ stageRef, controls, foamPackageType }: ME3DSceneProps) => {
  const { invalidate } = useThree();
  const maxStage = cameraKeyframes.positions.length - 1; // 3
  const initialStage = clamp(stageRef.current, 0, maxStage);

  const animationController = useRef({
    t: 0,
    startIdx: initialStage,
    endIdx: initialStage,
  });

  // We store stage space as numbers (can temporarily be -1 or 4 for wrap)
  const transition = useRef({
    from: initialStage,
    to: initialStage,
    progress: 1,
  });

  useFrame((state, delta) => {
    const targetStage = clamp(stageRef.current, 0, maxStage);
    const tr = transition.current;

    if (targetStage !== tr.to) {
      const currentFrom = tr.from;
      const currentTo = tr.to;
      const currentProgress = tr.progress;

      const currentIndex =
        currentProgress < 1 ? currentFrom : currentTo;

      tr.from = currentIndex;
      tr.to = targetStage;
      tr.progress = 0;
    }

    if (tr.progress < 1) {
      tr.progress += Math.min(delta, 0.064) / TRANSITION_DURATION;

      if (tr.progress >= 0.999) {
        tr.progress = 1;
      }

      // Self-sustain until transition is complete
      invalidate();
    }

    if (tr.progress === 1) {
      const finalStage = tr.to;

      animationController.current.startIdx = finalStage;
      animationController.current.endIdx = finalStage;
      animationController.current.t = 0;

      tr.from = finalStage; // lock clean state
    } else {
      const easeInOut = (x: number) =>
        x < 0.5
          ? 2 * x * x
          : 1 - Math.pow(-2 * x + 2, 2) / 2;

      const eased = easeInOut(tr.progress);

      animationController.current.startIdx = tr.from;
      animationController.current.endIdx = tr.to;
      animationController.current.t = eased;
    }

    const { startIdx, endIdx, t } = animationController.current;

    const targetPosition = cameraKeyframes.positions[startIdx]
      .clone()
      .lerp(cameraKeyframes.positions[endIdx], t);

    const targetLookAt = cameraKeyframes.lookAtPosition[startIdx]
      .clone()
      .lerp(cameraKeyframes.lookAtPosition[endIdx], t);

    state.camera.position.copy(targetPosition);
    state.camera.lookAt(targetLookAt);
  });

  const { isMobile, isTablet } = useResponsive();


  return (
    <>
      {/* Debug: red=X, green=Y, blue=Z */}
      {/* <axesHelper args={[2]} /> */}
      <GlbInsoleView
        key={1}
        insoleUri={getInsoleGlb(foamPackageType, 'RightFoot')}
        controller={animationController}
        keyframes={rightInsoleKeyframes}
        stageRef={stageRef}
        controls={
          controls && {
            archIndex: controls.rightArchIndex,
            isArchScanned: controls.isRightArchScanned,
            archNames: controls.archNames,
            archNamePlaceholder: controls.archNamePlaceholder,
            patternRotation: controls.rightStiffnessDir,
            patternPlaceholder: controls.stiffnessDirPlaceholder,
            zones: controls.rightZones,
          }
        }
        isLeft={false}
      />

      <GlbInsoleView
        key={2}
        insoleUri={getInsoleGlb(foamPackageType, 'LeftFoot')}
        controller={animationController}
        keyframes={leftInsoleKeyframes}
        stageRef={stageRef}
        controls={
          controls && {
            archIndex: controls.leftArchIndex,
            isArchScanned: controls.isLeftArchScanned,
            archNames: controls.archNames,
            archNamePlaceholder: controls.archNamePlaceholder,
            patternRotation: controls.leftStiffnessDir,
            patternPlaceholder: controls.stiffnessDirPlaceholder,
            zones: controls.leftZones,
          }
        }
        isLeft
      />

      {!isMobile && !isTablet && <AnnotationArrows stageRef={stageRef} />}
    </>
  );
};

interface InsoleControls {
  leftArchIndex: React.MutableRefObject<number | undefined>;
  rightArchIndex: React.MutableRefObject<number | undefined>;
  isLeftArchScanned: React.MutableRefObject<boolean | undefined>;
  isRightArchScanned: React.MutableRefObject<boolean | undefined>;
  archNames: string[];
  archNamePlaceholder: string;
  leftStiffnessDir: React.MutableRefObject<number | undefined>;
  rightStiffnessDir: React.MutableRefObject<number | undefined>;
  stiffnessDirPlaceholder: string;
  leftZones: React.MutableRefObject<any>;
  rightZones: React.MutableRefObject<any>;
}

interface ME3DSceneProps {
  foamPackageType: FoamPackageType;
  stageRef: React.MutableRefObject<number>;
  controls?: InsoleControls;
}

// This component no longer owns a <Canvas>. It portals its R3F scene content
// into the single PersistentCanvas via sceneTunnel.In so that switching
// routes does not destroy & recreate the WebGL context (the cause of the iOS
// Safari crashes after a few back/next toggles).
const ME3DScene = ({ foamPackageType, stageRef, controls }: ME3DSceneProps) => {
  return (
    <sceneTunnel.In>
      <CameraSetter near={0.1} far={100} fov={35} />
      <Environment files={studioHdr} environmentIntensity={0.3} />
      <Scene foamPackageType={foamPackageType} stageRef={stageRef} controls={controls} />
      <ContactShadows far={1} />
      {/* <OrbitControls /> */}
			{/* <axesHelper args={[5]} /> */}
			{/* <gridHelper args={[10, 10]} /> */}
    </sceneTunnel.In>
  );
};

export default ME3DScene;
